import React, { useEffect, useState } from 'react';
import { DollarSign, Calendar, Layers, AlertCircle, ChevronDown, ChevronUp, User } from 'lucide-react';
import { InfoTooltip } from '../../../common/InfoTooltip';
import { useAuth } from '../../../../contexts/AuthContext';
import CircularProgress from '../../../common/CircularProgress';
import { secureHttp } from '../../../../hooks/api/secureHttp';
import { mapSessionResponse, RawSessionItem } from '../../../../utils/sessionMapper';
import { SESSION_APPOINTMENT_TYPES, LIQUIDACIONES_WEBHOOK } from '../../../../hooks/api/config';
import { parseSessionDate } from '../../../../utils/sessions';
import { BA_TIMEZONE } from '../../../../utils/timezone';
import { usePsychologistContext } from '../../../../contexts/PsychologistContext';
import type { Session } from '../../../../types/session';
import { DatosDeCobroSection } from './DatosDeCobroSection';
import { PaymentDataFormModal } from './PaymentDataFormModal';
import type { LoadedPaymentInfo } from './DatosDeCobroSection';
import { usePaymentInfo } from '../../../../hooks/usePaymentInfo';
import { useToast } from '../../../../hooks/useToast';
import { Button } from '@/components/ui/button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import {
  RemuneracionZone,
  REMUNERACION_TIERS,
  REMUNERACION_ZONE_LABELS,
} from '../../../../utils/remuneracionTiers';
import { useNZSettlementSchedule } from '../../../../hooks/useNZSettlementSchedule';
import { NZ_MIN_THRESHOLD, NZSettlementMonthResult } from '../../../../utils/nzLiquidacion';

interface LiquidacionItem {
  Email: string;
  Tarifa: string;
  Periodo: string;
  Bonificacion: string;
  'Medio de pago': string;
  'Moneda de Pago': string;
  'Tipo de cambio': string;
  'Tipo de servicio': string;
  'Cantidad sesiones': string;
  'Precio por sesión': string;
  'Subtotal por servicio': string;
  'Monto en Moneda de Pago': string;
  'Canceladas ya liquidadas': string;
}

interface PeriodoGroup {
  periodo: string;
  servicios: LiquidacionItem[];
  total: number | null;
  monedaTotal: string;
  montoUSD: number | null;
  tipoCambio: string;
}

// El webhook devuelve el campo "liquidacion" con comillas sin escapar dentro del string,
// por lo que res.json() falla. Se extrae el objeto JSON crudo por posición de llaves.
// Si el chunk externo falla, avanzamos de a 1 para encontrar el próximo { interno.
function extractJsonObjects(text: string): LiquidacionItem[] {
  const results: LiquidacionItem[] = [];
  let i = 0;
  while (i < text.length) {
    const start = text.indexOf('{', i);
    if (start === -1) break;
    let depth = 0;
    let end = -1;
    for (let j = start; j < text.length; j++) {
      if (text[j] === '{') depth++;
      else if (text[j] === '}') {
        depth--;
        if (depth === 0) { end = j; break; }
      }
    }
    if (end === -1) break;
    const chunk = text.slice(start, end + 1);
    try {
      const obj = JSON.parse(chunk);
      // Nuevo formato de Make: { key, data: LiquidacionItem, __IMTINDEX__, __IMTLENGTH__ }
      // Formato anterior: el objeto plano directamente
      const item: LiquidacionItem = (obj.data?.Email || obj.data?.Periodo) ? obj.data : obj;
      if (item.Email || item.Periodo) {
        results.push(item);
      }
      i = end + 1;
    } catch {
      i = start + 1;
    }
  }
  return results;
}

const MESES: Record<string, number> = {
  enero: 1, febrero: 2, marzo: 3, abril: 4, mayo: 5, junio: 6,
  julio: 7, agosto: 8, septiembre: 9, octubre: 10, noviembre: 11, diciembre: 12,
};

function periodoToRango(periodo: string): string {
  // Formato esperado: "Abril 26" → 01/04/2026 al 30/04/2026
  const parts = periodo.trim().split(/\s+/);
  if (parts.length < 2) return periodo;
  const mes = MESES[parts[0].toLowerCase()];
  const anioCorto = parseInt(parts[1], 10);
  if (!mes || isNaN(anioCorto)) return periodo;
  const anio = 2000 + anioCorto;
  const ultimoDia = new Date(anio, mes, 0).getDate();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(1)}/${pad(mes)}/${anio} al ${pad(ultimoDia)}/${pad(mes)}/${anio}`;
}

function periodoToISORange(periodo: string): { from: string; to: string } | null {
  const parts = periodo.trim().split(/\s+/);
  if (parts.length < 2) return null;
  const mes = MESES[parts[0].toLowerCase()];
  const anioCorto = parseInt(parts[1], 10);
  if (!mes || isNaN(anioCorto)) return null;
  const anio = 2000 + anioCorto;
  const ultimoDia = new Date(anio, mes, 0).getDate();
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    from: `${anio}-${pad(mes)}-01 00:00:00`,
    to: `${anio}-${pad(mes)}-${pad(ultimoDia)} 23:59:59`,
  };
}

function groupByPeriodo(items: LiquidacionItem[]): PeriodoGroup[] {
  const map = new Map<string, LiquidacionItem[]>();

  for (const item of items) {
    const key = item.Periodo;
    if (!map.has(key)) map.set(key, []);

    const existing = map.get(key)!;
    // Deduplicar por tipo de servicio dentro del mismo periodo
    const alreadyHasTipo = existing.some(
      (e) => e['Tipo de servicio'] === item['Tipo de servicio']
    );
    if (!alreadyHasTipo) existing.push(item);
  }

  return Array.from(map.entries()).map(([periodo, servicios]) => {
    const primerConMonto = servicios.find((s) => parseFloat(s['Monto en Moneda de Pago']) > 0);
    const total = primerConMonto ? parseFloat(primerConMonto['Monto en Moneda de Pago']) : null;
    const monedaTotal = primerConMonto?.['Moneda de Pago'] ?? '';
    const tipoCambio = primerConMonto?.['Tipo de cambio'] ?? '';

    // Monto en USD = suma de subtotales + bonificaciones + canceladas ya liquidadas del período
    const parseNum = (v: string | number | undefined) => parseFloat(String(v ?? '').replace(/[^0-9.-]/g, '')) || 0;
    const montoUSD = servicios.reduce((acc, s) => {
      return acc + parseNum(s['Subtotal por servicio']) + parseNum(s['Bonificacion']) + parseNum(s['Canceladas ya liquidadas']);
    }, 0);

    return { periodo, servicios, total, monedaTotal, montoUSD: montoUSD > 0 ? montoUSD : null, tipoCambio };
  });
}

interface Props {
  /** Solo las psicólogas con ranking NZ (en el journey "Tu camino inicial") ven el bloque
   *  estático de info de pagos con el formato de la Carta Oferta, dividido en sub-tabs. El
   *  resto sigue viendo únicamente el listado dinámico de liquidaciones, como antes de este
   *  bloque — sin sub-tabs. */
  isNZ?: boolean;
}

type LiquidacionSubTab = 'info' | 'pago' | 'liquidaciones';

const LIQUIDACION_SUB_TABS: { id: LiquidacionSubTab; label: string }[] = [
  { id: 'info', label: 'Información general' },
  { id: 'pago', label: 'Tus datos para el pago' },
  { id: 'liquidaciones', label: 'Tus liquidaciones' },
];

export const MetricsLiquidacionTab: React.FC<Props> = ({ isNZ = false }) => {
  const { user } = useAuth();
  const { showSuccess } = useToast();
  const [grupos, setGrupos] = useState<PeriodoGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [subTab, setSubTab] = useState<LiquidacionSubTab>('info');

  const {
    info: paymentInfoResponse,
    isLoading: isPaymentInfoLoading,
    error: paymentInfoError,
    refetch: refetchPaymentInfo,
    setInfo: setPaymentInfoResponse,
  } = usePaymentInfo();
  const [isFormOpen, setIsFormOpen] = useState(false);

  const loadedPaymentInfo: LoadedPaymentInfo | null =
    paymentInfoResponse?.currency && paymentInfoResponse.data && paymentInfoResponse.status
      ? { data: paymentInfoResponse.data, status: paymentInfoResponse.status }
      : null;

  // `exists:true` + `currency:null`: medio de pago que esta integración no
  // administra (ej. EUR) — lo gestiona Finanzas directamente, distinto del
  // estado vacío (`exists:false`, sin nada cargado todavía).
  const managedByFinanzas = paymentInfoResponse?.exists === true && paymentInfoResponse.currency === null;

  useEffect(() => {
    if (!user?.email) return;

    setLoading(true);
    setError(false);

    fetch(LIQUIDACIONES_WEBHOOK, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        emailPsico: user.email,
        apiKey: process.env.REACT_APP_LIQUIDACIONES_API_KEY ?? '',
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('network error');
        return res.text();
      })
      .then((text) => {
        const items = extractJsonObjects(text);
        setGrupos(groupByPeriodo(items));
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [user?.email]);

  const latestPeriodo = grupos.reduce<string | null>((latest, g) => {
    const range = periodoToISORange(g.periodo);
    if (!range) return latest;
    if (!latest) return g.periodo;
    const latestRange = periodoToISORange(latest);
    return !latestRange || range.from > latestRange.from ? g.periodo : latest;
  }, null);

  const liquidacionesList = loading ? (
    <div className="flex justify-center items-center py-20">
      <CircularProgress label="Cargando liquidaciones..." />
    </div>
  ) : error ? (
    <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
      <div className="bg-red-100 rounded-2xl p-4">
        <AlertCircle size={28} className="text-red-500" />
      </div>
      <p className="text-base font-semibold text-gray-800">No pudimos cargar tus liquidaciones</p>
      <p className="text-sm text-gray-500">Intentá de nuevo en unos minutos.</p>
    </div>
  ) : grupos.length === 0 ? (
    <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
      <div className="bg-emerald-100 rounded-2xl p-4">
        <DollarSign size={28} className="text-emerald-500" />
      </div>
      <p className="text-base font-semibold text-gray-800">Todavía no tenés liquidaciones</p>
      <p className="text-sm text-gray-500">Acá vas a ver tus cobros cuando estén disponibles.</p>
    </div>
  ) : (
    grupos.map((grupo) => (
      <PeriodoCard key={grupo.periodo} grupo={grupo} isLatest={grupo.periodo === latestPeriodo} />
    ))
  );

  // Bloque de datos de cobro. Es independiente del journey NZ: las NZ lo ven dentro del sub-tab
  // "Tus datos para el pago" y el resto debajo de su listado de liquidaciones. Conecta contra
  // psima (GET/POST reales vía psiplatform-be) y es visible para toda psicóloga logueada.
  const datosDeCobroBlock = (
    <>
      {isPaymentInfoLoading ? (
        <div className="flex justify-center items-center py-10" data-testid="datos-cobro-loading">
          <CircularProgress label="Cargando datos de cobro..." />
        </div>
      ) : paymentInfoError ? (
        <div className="flex flex-col items-center justify-center py-10 gap-3 text-center" data-testid="datos-cobro-error">
          <div className="bg-red-100 rounded-2xl p-4">
            <AlertCircle size={24} className="text-red-500" />
          </div>
          <p className="text-sm font-semibold text-gray-800">No pudimos cargar tus datos de cobro</p>
          <Button variant="outline" size="sm" onClick={refetchPaymentInfo} data-testid="datos-cobro-retry-btn">
            Reintentar
          </Button>
        </div>
      ) : (
        <DatosDeCobroSection
          paymentInfo={loadedPaymentInfo}
          managedByFinanzas={managedByFinanzas}
          onLoad={() => setIsFormOpen(true)}
          onModify={() => setIsFormOpen(true)}
        />
      )}

      <PaymentDataFormModal
        open={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        initialData={loadedPaymentInfo}
        onSaved={info => {
          setPaymentInfoResponse({ exists: true, currency: info.data.currency, status: info.status, data: info.data });
          setIsFormOpen(false);
          showSuccess(
            info.status === 'incomplete'
              ? 'Guardamos tus datos, pero faltan algunos — tu liquidación podría demorarse.'
              : 'Datos de cobro guardados correctamente',
          );
        }}
      />
    </>
  );

  // Único gate de NZ: la prop `isNZ`, que baja de ProfileMetricsTab (journeyMode, que combina
  // el webhook de ranking actual/anterior + status de baja + everHadProcesses + egresoAck).
  // No agregar acá un chequeo propio sobre rankingCategory: eso fue el bug que arregló el
  // commit 4e380d1 ("O en baja por motivo ajeno a NZ veía igual Camino Inicial y pagos NZ").
  if (!isNZ) {
    return (
      <div className="flex flex-col gap-5">
        {liquidacionesList}
        {datosDeCobroBlock}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 overflow-x-auto">
        {LIQUIDACION_SUB_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setSubTab(tab.id)}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              subTab === tab.id
                ? 'bg-violet-600 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {subTab === 'info' && <RemuneracionInfoCard />}
      {subTab === 'pago' && (
        <div className="flex flex-col gap-5">
          <DatosPagoCard />
          {datosDeCobroBlock}
        </div>
      )}
      {subTab === 'liquidaciones' && (
        <div className="flex flex-col gap-5">
          <NZLiquidacionSchedule onGoToInfo={() => setSubTab('info')} />
        </div>
      )}
    </div>
  );
};

const REMUNERACION_ZONE_HEADER_STYLES: Record<RemuneracionZone, string> = {
  entrada: 'bg-violet-950 text-white',
  media: 'bg-violet-800 text-white',
  alta: 'bg-emerald-700 text-white',
};

const REMUNERACION_ZONE_PILL_STYLES: Record<RemuneracionZone, string> = {
  entrada: 'bg-violet-100 text-violet-700',
  media: 'bg-violet-100 text-violet-700',
  alta: 'bg-emerald-100 text-emerald-700',
};

const RemuneracionInfoCard: React.FC = () => (
  <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-5">
    <div className="flex items-center gap-2 mb-3">
      <div className="bg-emerald-100 rounded-lg p-1.5">
        <DollarSign size={16} className="text-emerald-600" />
      </div>
      <span className="font-semibold text-gray-800 text-base">Remuneración</span>
    </div>
    <p className="text-sm text-gray-600 mb-4">
      Acá va el modelo de pagos. Lo explicamos en detalle porque queremos que entiendas exactamente
      cómo funciona antes de arrancar.
    </p>

    <div className="grid grid-cols-3 gap-3 mb-4">
      {[
        { label: 'Umbral mínimo', value: '15 sesiones' },
        { label: 'Pago inicial', value: '$170 USD' },
        { label: 'Techo', value: '$2.000 USD' },
      ].map((stat) => (
        <div key={stat.label} className="bg-white border border-gray-200 rounded-xl text-center px-2 py-3">
          <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">{stat.label}</p>
          <p className="text-sm font-bold text-gray-900">{stat.value}</p>
        </div>
      ))}
    </div>

    <p className="font-semibold text-gray-900 underline mb-1.5">El modelo es por franja de sesiones tenidas</p>

    <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-xs font-semibold text-gray-700">Sesiones</TableHead>
            <TableHead className="text-xs font-semibold text-gray-700 text-right">Ingreso mensual</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {REMUNERACION_TIERS.map((tier, i) => {
            const prevZone = i > 0 ? REMUNERACION_TIERS[i - 1].zone : null;
            const isTopTier = tier.range === '130+';
            return (
              <React.Fragment key={tier.range}>
                {tier.zone !== prevZone && (
                  <TableRow>
                    <TableCell
                      colSpan={2}
                      className={`py-2 text-[11px] font-semibold uppercase tracking-wide ${REMUNERACION_ZONE_HEADER_STYLES[tier.zone]}`}
                    >
                      {REMUNERACION_ZONE_LABELS[tier.zone]}
                    </TableCell>
                  </TableRow>
                )}
                <TableRow className={isTopTier ? 'bg-emerald-50' : undefined}>
                  <TableCell>
                    <span
                      className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                        isTopTier ? 'bg-emerald-600 text-white' : REMUNERACION_ZONE_PILL_STYLES[tier.zone]
                      }`}
                    >
                      {tier.range}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-bold text-gray-900">{tier.amount} USD</TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>

    <p className="font-semibold text-gray-900 underline mb-1.5">Acumulación</p>
    <p className="text-sm text-gray-600 mb-3">
      Si en un mes hacés menos de 15 sesiones, no se pierden: se suman al mes siguiente. Cuando el
      total acumulado entra en un rango, se liquida ese monto y el contador vuelve a cero.
    </p>
    <div className="bg-gray-50 rounded-lg px-3 py-2.5 mb-3">
      <p className="text-sm text-gray-700">
        <strong className="font-medium text-gray-900">Ejemplo:</strong> en enero hiciste 6 sesiones.
        En febrero hiciste 10. En febrero tenés 16 sesiones acumuladas y cobrás $170.
      </p>
    </div>
    <div className="border-l-2 border-gray-200 pl-3 mb-4">
      <p className="text-sm font-medium text-gray-800 mb-1">¿Y si hago pocas sesiones durante varios meses?</p>
      <p className="text-sm text-gray-600">
        Si después de dos meses completos el acumulado todavía no llegó a 15 sesiones, podés pedir
        una liquidación anticipada. En ese caso, se liquidan $50 por cada 5 sesiones realizadas.
      </p>
    </div>

    <p className="font-semibold text-gray-900 underline mb-1.5">Cuándo y cómo se paga</p>
    <p className="text-sm text-gray-600">
      Al último día de cada mes, el sistema registra en qué franja de sesiones estás. Ese monto se
      liquida dentro de los primeros 5 días del mes siguiente.
    </p>
  </div>
);

const DatosPagoCard: React.FC = () => (
  <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-5">
    <div className="flex items-center gap-2 mb-3">
      <div className="bg-emerald-100 rounded-lg p-1.5">
        <DollarSign size={16} className="text-emerald-600" />
      </div>
      <span className="font-semibold text-gray-800 text-base">Tus datos para el pago</span>
    </div>
    <p className="text-sm text-gray-600">
      Todos los pagos son en dólares USD: transferencia internacional o billetera USDT. Si
      necesitás asistencia para crear alguna de estas cuentas, te vamos a ayudar más adelante en
      el proceso.
    </p>
  </div>
);

const PeriodoCard: React.FC<{ grupo: PeriodoGroup; isLatest: boolean }> = ({ grupo, isLatest }) => {
  const [open, setOpen] = useState(false);
  const esMonedaExtranjera = grupo.monedaTotal !== '' && grupo.monedaTotal !== 'USD';

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      {/* Header clickeable */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 bg-gradient-to-r from-emerald-50 to-teal-50 hover:from-emerald-100 hover:to-teal-100 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <div className="bg-emerald-100 rounded-lg p-1.5">
            <Calendar size={16} className="text-emerald-600" />
          </div>
          <span className="font-semibold text-gray-800 text-base">{grupo.periodo}</span>
        </div>

        <div className="flex items-center gap-4">
          {grupo.total !== null && (
            <div className="text-right">
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Total a cobrar</p>
              <p className="text-lg font-bold text-emerald-600">
                {grupo.monedaTotal} {grupo.total.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              {/* Monto en USD solo si la moneda de pago no es USD */}
              {esMonedaExtranjera && grupo.montoUSD !== null && (
                <p className="text-xs text-gray-500 font-medium mt-0.5">
                  ≈ USD {grupo.montoUSD.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              )}
              {/* Tipo de cambio solo si moneda ≠ USD */}
              {esMonedaExtranjera && grupo.tipoCambio && (
                <p className="text-xs text-gray-400 mt-0.5">TC: {grupo.tipoCambio}</p>
              )}
            </div>
          )}
          <div className="text-gray-400">
            {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </div>
        </div>
      </button>

      {/* Detalle colapsable */}
      {open && (
        <div className="px-5 py-3 flex flex-col gap-3 border-t border-gray-100">
          {grupo.servicios.map((servicio) => (
            <ServicioRow key={servicio['Tipo de servicio']} servicio={servicio} periodo={grupo.periodo} isLatest={isLatest} />
          ))}
        </div>
      )}
    </div>
  );
};

const ServicioRow: React.FC<{ servicio: LiquidacionItem; periodo: string; isLatest: boolean }> = ({ servicio, periodo, isLatest }) => {
  const subtotal = servicio['Subtotal por servicio'];
  const bonificacion = servicio['Bonificacion'];
  const rango = periodoToRango(periodo);
  const [expanded, setExpanded] = useState(false);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [sessionsLoaded, setSessionsLoaded] = useState(false);
  const { psychologist } = usePsychologistContext();
  // Liquidación en el huso configurado por la psicóloga; Argentina como fallback
  const therapistTz = psychologist?.timezone || BA_TIMEZONE;

  const handleToggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !sessionsLoaded) {
      const range = periodoToISORange(periodo);
      if (!range) { setSessionsLoaded(true); return; }
      setLoadingSessions(true);
      try {
        // El endpoint devuelve un array directo. El filtro `from` no está soportado,
        // se usa solo `to` y se filtra por rango en el cliente.
        const resp = await secureHttp.get<RawSessionItem[] | { results?: RawSessionItem[] }>(
          '/v1/psi-platform/psychologist/sessions',
          { status: 'settled', to: range.to, take: 500 }
        );
        const rawItems: RawSessionItem[] = Array.isArray(resp)
          ? resp
          : Array.isArray((resp as { results?: RawSessionItem[] }).results)
          ? ((resp as { results?: RawSessionItem[] }).results ?? [])
          : [];
        const tipoServicio = servicio['Tipo de servicio'];
        const appointmentType = SESSION_APPOINTMENT_TYPES.find((t) => t.name === tipoServicio);
        const clinicalServiceId = appointmentType?.clinical_service;
        const fromTs = new Date(range.from).getTime();
        const toTs = new Date(range.to).getTime();
        const filtered = rawItems
          .filter((raw) => {
            const proc = raw.process;
            if (typeof proc !== 'object' || proc === null) return false;
            return clinicalServiceId ? proc.type?.id === clinicalServiceId : proc.type?.name === tipoServicio;
          })
          .map(mapSessionResponse)
          .filter((s) => {
            const t = new Date(s.date).getTime();
            return t >= fromTs && t <= toTs;
          })
          .sort((a, b) => a.date.localeCompare(b.date));
        setSessions(filtered);
      } catch {
        setSessions([]);
      } finally {
        setLoadingSessions(false);
        setSessionsLoaded(true);
      }
    }
  };

  return (
    <div className="rounded-xl bg-gray-50 px-4 py-3 flex flex-col gap-2">
      {/* Tipo de servicio + sesiones */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="bg-violet-100 rounded-md p-1">
            <Layers size={13} className="text-violet-500" />
          </div>
          <span className="text-sm font-semibold text-gray-800">{servicio['Tipo de servicio']}</span>
        </div>
        <button
          type="button"
          onClick={handleToggle}
          className="flex items-center gap-1 text-xs text-violet-600 font-medium hover:text-violet-800 transition-colors"
        >
          {servicio['Cantidad sesiones']} sesiones
          {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
      </div>

      {/* Detalle financiero */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs pl-1">
        <span className="text-gray-400 flex items-center gap-1">
          Precio por sesión
          {isLatest && servicio['Tarifa'] === 'USD 1' && servicio['Tipo de servicio'] === 'Individual' && (
            <InfoTooltip text="✨ Gracias a tu trayectoria y permanencia en Psi Mammoliti, contás con una tarifa significativamente superior a la base vigente por sesión realizada." />
          )}
          {isLatest && servicio['Tipo de servicio'] === 'Pareja' && parseFloat(String(servicio['Precio por sesión']).replace(/[^0-9.-]/g, '')) === 25 && (
            <InfoTooltip text="✨ Gracias a tu trayectoria y permanencia en Psi Mammoliti, contás con una tarifa significativamente superior a la base vigente por sesión realizada." />
          )}
        </span>
        <span className="font-medium text-right text-gray-700">{servicio['Precio por sesión']}</span>

        <span className="text-gray-400">Subtotal</span>
        <span className="font-medium text-right text-gray-700">{subtotal || '-'}</span>

        {bonificacion && bonificacion !== '0' && bonificacion !== '' && (
          <>
            <span className="text-gray-400">Bonificación</span>
            <span className="font-medium text-right text-emerald-600">{bonificacion}</span>
          </>
        )}
      </div>


      {/* Lista de sesiones expandible */}
      {expanded && (
        <div className="mt-1 border-t border-gray-200 pt-2 flex flex-col gap-1">
          <p className="text-xs text-gray-400 mb-1">Sesiones del {rango}</p>
          {loadingSessions ? (
            <div className="flex justify-center py-3">
              <CircularProgress />
            </div>
          ) : sessions.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-2">Sin sesiones registradas</p>
          ) : (
            sessions.map((s) => {
              const patient = s.patients?.[0];
              const patientName = patient
                ? `${patient.name ?? ''} ${(patient as { surname?: string }).surname ?? ''}`.trim()
                : '—';
              const dateObj = parseSessionDate(s.date);
              const dateLabel = dateObj.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: therapistTz });
              const timeLabel = dateObj.toLocaleString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: therapistTz }).replace(/\u202f/g, " ").toLowerCase();
              return (
                <div key={s.id} className="flex items-center justify-between py-1 px-2 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-2">
                    <User size={11} className="text-gray-400 shrink-0" />
                    <span className="text-xs text-gray-700">{patientName}</span>
                  </div>
                  <span className="text-xs text-gray-400">{dateLabel} {timeLabel}</span>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

function formatMonthLabel(month: string): string {
  const [year, monthNum] = month.split('-').map(Number);
  const label = new Date(year, monthNum - 1, 1).toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function formatDueDate(date: Date): string {
  return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

interface NZLiquidacionScheduleProps {
  /** Muestra el link "¿Cómo funciona este esquema?" que navega a la sub-tab Información general. */
  onGoToInfo?: () => void;
}

/** Perfil > Tu estadía en Psi Mammoliti > Liquidación > Tus liquidaciones. Se monta solo dentro
 *  del branch `isNZ` de MetricsLiquidacionTab (gate central, ver ProfileMetricsTab):
 *  liquidación calculada on-the-fly (useNZSettlementSchedule) del mes más reciente por
 *  default, con selector para navegar a meses anteriores desde julio 2026. */
const NZLiquidacionSchedule: React.FC<NZLiquidacionScheduleProps> = ({ onGoToInfo }) => {
  const { schedule, sessionsByMonth, loading, error, refetch } = useNZSettlementSchedule();
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);

  const latestMonth = schedule.length > 0 ? schedule[schedule.length - 1].month : null;
  const activeMonth = selectedMonth && schedule.some((r) => r.month === selectedMonth) ? selectedMonth : latestMonth;
  const activeResult = schedule.find((r) => r.month === activeMonth) ?? null;

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <CircularProgress label="Cargando tus liquidaciones..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
        <div className="bg-red-100 rounded-2xl p-4">
          <AlertCircle size={28} className="text-red-500" />
        </div>
        <p className="text-base font-semibold text-gray-800">No pudimos cargar tus liquidaciones</p>
        <p className="text-sm text-gray-500">Intentá de nuevo en unos minutos.</p>
        <button
          type="button"
          onClick={refetch}
          className="mt-1 px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors"
          data-testid="nz-liquidacion-reintentar"
        >
          Reintentar
        </button>
      </div>
    );
  }

  if (!activeResult) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
        <div className="bg-emerald-100 rounded-2xl p-4">
          <DollarSign size={28} className="text-emerald-500" />
        </div>
        <p className="text-base font-semibold text-gray-800">Todavía no tenés liquidaciones</p>
        <p className="text-sm text-gray-500">Acá vas a ver tus cobros cuando estén disponibles.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="nz-liquidacion-schedule">
      <Select value={activeMonth ?? undefined} onValueChange={setSelectedMonth}>
        <SelectTrigger className="w-full sm:w-64" data-testid="nz-liquidacion-mes-selector">
          <SelectValue placeholder="Seleccionar mes" />
        </SelectTrigger>
        <SelectContent>
          {[...schedule].reverse().map((r) => (
            <SelectItem key={r.month} value={r.month}>
              {formatMonthLabel(r.month)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <NZLiquidacionMonthCard result={activeResult} schedule={schedule} sessionsByMonth={sessionsByMonth} />

      {onGoToInfo && (
        <button
          type="button"
          onClick={onGoToInfo}
          className="text-sm text-violet-600 font-medium hover:text-violet-800 hover:underline transition-colors text-left"
          data-testid="nz-liquidacion-como-funciona"
        >
          ¿Cómo funciona este esquema?
        </button>
      )}
    </div>
  );
};

type NZStatusBadgeKey = 'accumulating' | 'pending' | 'paid';

const NZ_STATUS_BADGE: Record<NZStatusBadgeKey, { label: string; className: string; description: string }> = {
  accumulating: {
    label: 'Acumulando',
    className: 'bg-amber-100 text-amber-700',
    description: 'Todavía estás sumando sesiones para esta liquidación. Se acumulan mes a mes hasta llegar al mínimo de 15.',
  },
  pending: {
    label: 'Por pagar',
    className: 'bg-red-100 text-red-700',
    description: 'Ya se calculó el monto de este período. Se gestiona dentro de los primeros días del mes siguiente.',
  },
  paid: {
    label: 'Pagada',
    className: 'bg-emerald-100 text-emerald-700',
    description: 'Este pago ya fue gestionado de nuestro lado. Según tu método de cobro, puede demorar entre 24 y 72hs en verse reflejado.',
  },
};

const NZLiquidacionMonthCard: React.FC<{
  result: NZSettlementMonthResult;
  schedule: NZSettlementMonthResult[];
  sessionsByMonth: Record<string, Session[]>;
}> = ({ result, schedule, sessionsByMonth }) => {
  const badgeKey: NZStatusBadgeKey = !result.crossedThreshold ? 'accumulating' : result.status;
  const badge = NZ_STATUS_BADGE[badgeKey];

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-5 flex flex-col gap-4" data-testid="nz-liquidacion-mes-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="bg-emerald-100 rounded-lg p-1.5">
            <Calendar size={16} className="text-emerald-600" />
          </div>
          <span className="font-semibold text-gray-800 text-base" data-testid="nz-liquidacion-mes-label">
            {formatMonthLabel(result.month)}
          </span>
        </div>
        <Tooltip delayDuration={150}>
          <TooltipTrigger asChild>
            <span
              tabIndex={0}
              className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold cursor-help ${badge.className}`}
              data-testid="nz-liquidacion-status-badge"
            >
              {badge.label}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={6} className="w-[240px] py-2 leading-relaxed" data-testid="nz-liquidacion-status-tooltip">
            {badge.description}
          </TooltipContent>
        </Tooltip>
      </div>

      {result.crossedThreshold && result.tier ? (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-xl px-3 py-2.5">
              <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">Sesiones del mes</p>
              <p className="text-sm font-bold text-gray-900" data-testid="nz-liquidacion-sesiones">{result.sessionsThisMonth}</p>
            </div>
            <div className="bg-gray-50 rounded-xl px-3 py-2.5">
              <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">Acumulado considerado</p>
              <p className="text-sm font-bold text-gray-900">{result.totalConsideredForThreshold}</p>
            </div>
          </div>
          <div>
            <span
              className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide ${REMUNERACION_ZONE_HEADER_STYLES[result.tier.zone]}`}
              data-testid="nz-liquidacion-zona-pill"
            >
              {REMUNERACION_ZONE_LABELS[result.tier.zone]}
            </span>
          </div>
          <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2.5">
            <span className="text-sm text-gray-600">Monto a liquidar</span>
            <span className="text-lg font-bold text-emerald-600" data-testid="nz-liquidacion-monto">
              USD {result.amountToSettle?.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Fecha de pago</span>
            <span className="font-medium text-gray-800" data-testid="nz-liquidacion-fecha-pago">
              {formatDueDate(result.dueDate)}
            </span>
          </div>
        </>
      ) : (
        <NZAcumulandoZonasView result={result} schedule={schedule} sessionsByMonth={sessionsByMonth} />
      )}
    </div>
  );
};

// Debajo de este número de sesiones faltantes para el umbral mínimo, se muestra el incentivo
// visual ("¡Estás cerca!"). Puramente de UI — no es parte de la regla de negocio de
// calculateNZSettlementSchedule, por eso vive acá y no en nzLiquidacion.ts.
const NZ_CLOSE_TO_THRESHOLD_SESSIONS = 5;

/** Meses ('YYYY-MM', orden cronológico) que componen la racha de acumulación vigente en
 *  `result` — desde el primer mes que empezó a acumular sin cortarse (sin cruzar el umbral)
 *  hasta `result.month` inclusive. `result.monthsWithoutThreshold` (ya calculado por
 *  calculateNZSettlementSchedule) es exactamente el largo de esa racha, así que no hace falta
 *  reimplementar la lógica de acumulación acá — solo recortar `schedule` con ese largo. */
function getStreakMonths(schedule: NZSettlementMonthResult[], result: NZSettlementMonthResult): string[] {
  const index = schedule.findIndex((r) => r.month === result.month);
  if (index === -1) return [result.month];
  const start = Math.max(0, index - (result.monthsWithoutThreshold - 1));
  return schedule.slice(start, index + 1).map((r) => r.month);
}

/** Mientras un mes está "Acumulando" (no cruzó ningún umbral todavía), la próxima zona a
 *  alcanzar es SIEMPRE la primera de REMUNERACION_TIERS ('15 – 19') — ese es justamente el
 *  único tier que arranca en el umbral mínimo (15), y crossedThreshold=false implica que el
 *  acumulado todavía no llega ahí. No hace falta buscarla: no existe otro tier alcanzable
 *  todavía. Replica visualmente la tabla de RemuneracionInfoCard (mismo REMUNERACION_TIERS,
 *  mismos estilos de sección) resaltando esa primera fila con el progreso integrado, y
 *  atenuando el resto de las zonas debajo — Sesiones del mes/Acumulado considerado quedan
 *  integrados en el texto de progreso en vez de mostrarse en cajas aparte. */
const NZAcumulandoZonasView: React.FC<{
  result: NZSettlementMonthResult;
  schedule: NZSettlementMonthResult[];
  sessionsByMonth: Record<string, Session[]>;
}> = ({ result, schedule, sessionsByMonth }) => {
  const { psychologist } = usePsychologistContext();
  const therapistTz = psychologist?.timezone || BA_TIMEZONE;
  const [sessionsOpen, setSessionsOpen] = useState(false);

  const remaining = Math.max(0, NZ_MIN_THRESHOLD - result.totalConsideredForThreshold);
  const progressPct = Math.max(0, Math.min(100, Math.round((result.totalConsideredForThreshold / NZ_MIN_THRESHOLD) * 100)));
  const isCloseToThreshold = remaining > 0 && remaining <= NZ_CLOSE_TO_THRESHOLD_SESSIONS;
  const sesionLabel = remaining === 1 ? 'sesión' : 'sesiones';
  const faltaLabel = remaining === 1 ? 'falta' : 'faltan';
  const streakMonths = getStreakMonths(schedule, result);

  return (
    <div className="flex flex-col gap-3" data-testid="nz-liquidacion-acumulando">
      <p className="text-sm text-amber-800">
        Cada sesión te acerca a tu próxima liquidación — seguís sumando hasta llegar a la zona de entrada.
      </p>

      {isCloseToThreshold && (
        <div
          className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5 flex items-center gap-2"
          data-testid="nz-liquidacion-incentivo"
        >
          <span aria-hidden="true" className="text-base">🔥</span>
          <p className="text-sm font-semibold text-emerald-800">
            ¡Estás cerca! Te {faltaLabel} {remaining} {sesionLabel} para tu próxima liquidación.
          </p>
        </div>
      )}

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs font-semibold text-gray-700">Sesiones</TableHead>
              <TableHead className="text-xs font-semibold text-gray-700 text-right">Ingreso mensual</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {REMUNERACION_TIERS.map((tier, i) => {
              const isNextZone = i === 0;
              const prevZone = i > 0 ? REMUNERACION_TIERS[i - 1].zone : null;
              return (
                <React.Fragment key={tier.range}>
                  {tier.zone !== prevZone && (
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        className={`py-2 text-[11px] font-semibold uppercase tracking-wide ${
                          isNextZone ? REMUNERACION_ZONE_HEADER_STYLES[tier.zone] : 'bg-gray-100 text-gray-400'
                        }`}
                      >
                        {REMUNERACION_ZONE_LABELS[tier.zone]}
                      </TableCell>
                    </TableRow>
                  )}
                  {isNextZone ? (
                    <TableRow>
                      <TableCell colSpan={2} className="p-0">
                        <div
                          className="px-3 py-3 bg-violet-50 border-l-4 border-violet-600"
                          data-testid="nz-liquidacion-proxima-zona"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span
                              className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold bg-violet-600 text-white"
                              data-testid="nz-liquidacion-zona-objetivo"
                            >
                              {tier.range}
                            </span>
                            <span className="text-sm font-bold text-gray-900">{tier.amount} USD</span>
                          </div>
                          <div
                            className="w-full bg-violet-100 rounded-full h-2 overflow-hidden mb-1.5"
                            data-testid="nz-liquidacion-progreso-barra"
                            data-progress={progressPct}
                            role="progressbar"
                            aria-valuenow={progressPct}
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-label={`Progreso hacia el umbral mínimo de ${NZ_MIN_THRESHOLD} sesiones`}
                          >
                            <div className="bg-violet-600 h-2 rounded-full" style={{ width: `${progressPct}%` }} />
                          </div>

                          <button
                            type="button"
                            onClick={() => setSessionsOpen((v) => !v)}
                            className="w-full flex items-center justify-between gap-2 text-left"
                            data-testid="nz-liquidacion-progreso-toggle"
                            aria-expanded={sessionsOpen}
                          >
                            <p className="text-xs text-violet-700 font-medium" data-testid="nz-liquidacion-progreso-texto">
                              Llevás {result.totalConsideredForThreshold} de {NZ_MIN_THRESHOLD} sesiones
                              {result.carriedOverFromPreviousMonths > 0 && (
                                <span className="text-violet-500">
                                  {' '}({result.sessionsThisMonth} este mes + {result.carriedOverFromPreviousMonths} acumuladas)
                                </span>
                              )}
                            </p>
                            {sessionsOpen ? (
                              <ChevronUp size={14} className="text-violet-500 shrink-0" />
                            ) : (
                              <ChevronDown size={14} className="text-violet-500 shrink-0" />
                            )}
                          </button>

                          {sessionsOpen && (
                            <div className="mt-2 pt-2 border-t border-violet-200 flex flex-col gap-2" data-testid="nz-liquidacion-sesiones-detalle">
                              {streakMonths.map((month) => {
                                const monthSessions = sessionsByMonth[month] ?? [];
                                return (
                                  <div key={month}>
                                    <p className="text-[10px] font-semibold uppercase tracking-wide text-violet-600 mb-1">
                                      {formatMonthLabel(month)}
                                    </p>
                                    {monthSessions.length === 0 ? (
                                      <p className="text-xs text-violet-400 pl-1">Sin sesiones registradas</p>
                                    ) : (
                                      <div className="flex flex-col gap-0.5">
                                        {monthSessions.map((s) => {
                                          const patient = s.patients?.[0];
                                          const patientName = patient?.name?.trim() || '—';
                                          const dateObj = parseSessionDate(s.date);
                                          const dateLabel = dateObj.toLocaleDateString('es-AR', {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric',
                                            timeZone: therapistTz,
                                          });
                                          return (
                                            <div
                                              key={s.id}
                                              className="flex items-center justify-between py-1 px-2 rounded-lg hover:bg-violet-100/60 transition-colors"
                                            >
                                              <div className="flex items-center gap-1.5">
                                                <User size={11} className="text-violet-400 shrink-0" />
                                                <span className="text-xs text-violet-700">{patientName}</span>
                                              </div>
                                              <span className="text-xs text-violet-400">{dateLabel}</span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow className="opacity-50">
                      <TableCell>
                        <span className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-400">
                          {tier.range}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-semibold text-gray-400">{tier.amount} USD</TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default MetricsLiquidacionTab;
